/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Renderable from "App/render/webgl/renderables/renderable";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import RenderNode from "App/render/webgl/nodes/render_node";
import {vec2} from "gl-matrix";
import MemoryManagement from "App/memory_management";
import WebGLContext from "App/web_gl_context";
import ImageDataLoader from "App/utils/textures/image_data_loader";
import {EDataTypeType} from "App/geometry/material/texture";
import Canvas2DContext from "App/canvas_2d_context";
import PVRImageDataLoader from "App/utils/textures/pvr_image_loader";
import DDSImageDataLoader from "App/utils/textures/dds_image_loader";
import ETC1ImageDataLoader from "App/utils/textures/etc1_image_loader";
import {checkGLError} from "App/utils/utils";
import Pass from "App/render/webgl/passes/pass";
import Factory from "App/render/webgl/renderables/factory";
import IScene from "js_interface/src/scene_interface";

export default
class Texture extends Renderable {

    public static TypeName: string = "Texture";
    public static emptyTextureDataCache: { [key: string]: Uint8Array } = {["512-512-3"]: new Uint8Array(512 * 512 * 3)};

    private _props: any = null;
    private cache: { model: number; dimension: vec2 | number[]; glRepeatS: GLenum; glRepeatT: GLenum };
    private _glTextureID: WebGLTexture = null;
    private _gpuSize: number = 0;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aRenderNode: RenderNode) {
        super(aSceneAPI, aRenderGraph, aRenderNode);

        // default values
        this.cache = {
            model: 4,
            dimension: [0, 0],
            glRepeatS: 0,
            glRepeatT: 0
        };
    }


    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aRenderNode: RenderNode): Texture {
        return new Texture(aSceneAPI, aRenderGraph, aRenderNode);
    }

    public release() {
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        if (this._glTextureID) {
            gl.deleteTexture(this._glTextureID);

            MemoryManagement.removeTextureObject(this._gpuSize);
        }
        this._glTextureID = null;
    }

    public setProperties(aProps: any) {
        this._props = aProps;

        // here w set the properties which need the gl context
        if (aProps) {
            let gl = (<WebGLContext>this._context).getWebGLRenderingContext();
            this.cache.glRepeatS = this._props.repeatS ? gl.REPEAT : gl.CLAMP_TO_EDGE;
            this.cache.glRepeatT = this._props.repeatT ? gl.REPEAT : gl.CLAMP_TO_EDGE;
            this.cache.model = function (aModel) {
                switch (aModel) {
                    case "MODULATE":
                        return 1;
                    case "CADWORK_MODULATE":
                        return 2;
                    case "BLEND":
                        return 3;
                    case "DECAL":
                    case "REPLACE":
                        return 4;
                    default:
                        return 2; // default model is CADWORK_MODULATE
                }
            }(this._props.model);
        }
    }

    private _setAnisotropyFiltering() {
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        let extAnis = WebGLContext.getFeatureAsExtension("TEXTURE_FILTER_ANISOTROPIC");
        if (extAnis) {
            gl.texParameterf(
                gl.TEXTURE_2D,
                extAnis.TEXTURE_MAX_ANISOTROPY_EXT,
                WebGLContext.caps.MAX_TEXTURE_MAX_ANISOTROPY_EXT_VALUE
            );
        }
    }

    public generateMipmaps() {
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        gl.bindTexture(gl.TEXTURE_2D, this._glTextureID);
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.bindTexture(gl.TEXTURE_2D, null);
    }

    public uploadTile(aTextureProperties: any, aRow: number, aCol: number, aTile: any): boolean {
        if (!aTile.data) {
            return;
        }

        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        gl.bindTexture(gl.TEXTURE_2D, this._glTextureID);

        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        this._setAnisotropyFiltering();
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

        let numMipmaps = ImageDataLoader.getNumMipmapsFromResolution(aTextureProperties.width, aTextureProperties.height);

        switch (aTextureProperties.dataType) {

            case EDataTypeType.imageTexture:
                gl.texSubImage2D(gl.TEXTURE_2D,
                    0,
                    aCol * aTile.width,
                    aRow * aTile.height,
                    aTextureProperties.alpha ? gl.RGBA : gl.RGB, // dom image has always alpha value
                    gl.UNSIGNED_BYTE,
                    aTile.data);
                break;

            case EDataTypeType.pvrImageTexture:
                let pvrtc = WebGLContext.getFeatureAsExtension("COMPRESSED_TEXTURE_PVRTC");
                for (let level = 0; level < numMipmaps; level++) {
                    gl.compressedTexSubImage2D(gl.TEXTURE_2D,
                        level,
                        aCol * aTile.width,
                        aRow * aTile.height,
                        aTile.data.mipmaps[level].width,
                        aTile.data.mipmaps[level].height,
                        aTextureProperties.alpha ? pvrtc.COMPRESSED_RGBA_PVRTC_4BPPV1_IMG : pvrtc.COMPRESSED_RGB_PVRTC_4BPPV1_IMG,
                        aTile.data.mipmaps[level].data
                    );
                }
                break;

            case EDataTypeType.ddsImageTexture:
            case EDataTypeType.crunchImageTexture:
                let s3tc = WebGLContext.getFeatureAsExtension("COMPRESSED_TEXTURE_S3TC");
                for (let level = 0; level < numMipmaps; level++) {
                    if(aTile.data.mipmaps[level].data.byteLength > 0) {
                        gl.compressedTexSubImage2D(gl.TEXTURE_2D,
                            level,
                            aCol * aTile.width,
                            aRow * aTile.height,
                            aTile.data.mipmaps[level].width,
                            aTile.data.mipmaps[level].height,
                            aTextureProperties.alpha ? s3tc.COMPRESSED_RGBA_S3TC_DXT5_EXT : s3tc.COMPRESSED_RGB_S3TC_DXT1_EXT,
                            aTile.data.mipmaps[level].data
                        );
                    }
                }
                break;

            case EDataTypeType.etc1ImageTexture:
                let etc1 = WebGLContext.getFeatureAsExtension("COMPRESSED_TEXTURE_ETC1");
                for (let level = 0; level < numMipmaps; level++) {
                    // compressedTexSubImage2D is not supported for etc1 textures which is not problem as with compressed textures we do not use the tiled approach
                    gl.compressedTexImage2D(gl.TEXTURE_2D,
                        level,
                        etc1.COMPRESSED_RGB_ETC1_WEBGL,
                        aTile.data.mipmaps[level].width,
                        aTile.data.mipmaps[level].height,
                        0,
                        aTile.data.mipmaps[level].data
                    );
                }
                break;
        }
        gl.bindTexture(gl.TEXTURE_2D, null);

        return true;
    }

    public static createTempTexture(aContext: WebGLContext | Canvas2DContext): WebGLTexture {
        let gl = (<WebGLContext>aContext).getWebGLRenderingContext();

        let tempTextureID = gl.createTexture();

        gl.bindTexture(gl.TEXTURE_2D, tempTextureID);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

        let racistTexture = new Uint8Array(3);
        racistTexture[0] = 255;
        racistTexture[1] = 255;
        racistTexture[2] = 255;
        gl.texImage2D(gl.TEXTURE_2D,
            0,
            gl.RGB,
            1,
            1,
            0,
            gl.RGB,
            gl.UNSIGNED_BYTE,
            racistTexture
        );

        return tempTextureID;
    }

    public createEmptyTexture(aTextureProperties: any): boolean {
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        this._glTextureID = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, this._glTextureID);

        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        this._setAnisotropyFiltering();
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

        let numMipmaps = ImageDataLoader.getNumMipmapsFromResolution(aTextureProperties.width, aTextureProperties.height);

        switch (aTextureProperties.dataType) {
            case EDataTypeType.imageTexture:
                let emptyDataKey = aTextureProperties.width + "-" + aTextureProperties.height + "-" + (aTextureProperties.alpha ? 4 : 3);
                let emptyData = Texture.emptyTextureDataCache[emptyDataKey];
                if (emptyData === undefined) {
                    emptyData = new Uint8Array(aTextureProperties.width * aTextureProperties.height * (aTextureProperties.alpha ? 4 : 3));
                }
                gl.texImage2D(gl.TEXTURE_2D,
                    0,
                    aTextureProperties.alpha ? gl.RGBA : gl.RGB,
                    aTextureProperties.width,
                    aTextureProperties.height,
                    0,
                    aTextureProperties.alpha ? gl.RGBA : gl.RGB,
                    gl.UNSIGNED_BYTE,
                    emptyData
                );

                // we need to init the texture pixels here otherwise Firefox will complain with opengl error
                // such taht the texture is not initialized before the texsubimage2d is used.
                gl.generateMipmap(gl.TEXTURE_2D); // this is needed here otherwise the webgl renderer complains that the mipmaps are not generated but the min filtering requires them

                this._gpuSize = MemoryManagement.addTextureObject(aTextureProperties.width, aTextureProperties.height, aTextureProperties.alpha ? 4 : 3, true);
                break;

            case EDataTypeType.pvrImageTexture:
                for (let level = 0; level < numMipmaps; level++) {
                    let width = Math.max(1, aTextureProperties.width / Math.pow(2, level));
                    let height = Math.max(1, aTextureProperties.height / Math.pow(2, level));
                    let pvrtc = WebGLContext.getFeatureAsExtension("COMPRESSED_TEXTURE_PVRTC");

                    let pixels = new Uint8Array(PVRImageDataLoader.getSizeFromResolution(width, 4));
                    gl.compressedTexImage2D(gl.TEXTURE_2D,
                        level,
                        aTextureProperties.alpha ? pvrtc.COMPRESSED_RGBA_PVRTC_4BPPV1_IMG : pvrtc.COMPRESSED_RGB_PVRTC_4BPPV1_IMG,
                        width,
                        height,
                        0,
                        pixels
                    );

                    this._gpuSize += MemoryManagement.addCompressedTextureObject(pixels.length);
                }
                break;

            case EDataTypeType.ddsImageTexture:
            case EDataTypeType.crunchImageTexture:
                for (let level = 0; level < numMipmaps; level++) {
                    let width = Math.max(1, aTextureProperties.width / Math.pow(2, level));
                    let height = Math.max(1, aTextureProperties.height / Math.pow(2, level));
                    let s3tc = WebGLContext.getFeatureAsExtension("COMPRESSED_TEXTURE_S3TC");

                    let pixels = new Uint8Array(DDSImageDataLoader.getSizeFromResolution(
                        width,
                        height,
                        aTextureProperties.alpha ? "dxt5" : "dxt1"));

                    gl.compressedTexImage2D(gl.TEXTURE_2D,
                        level,
                        aTextureProperties.alpha ? s3tc.COMPRESSED_RGBA_S3TC_DXT5_EXT : s3tc.COMPRESSED_RGB_S3TC_DXT1_EXT,
                        width,
                        height,
                        0,
                        pixels
                    );

                    this._gpuSize += MemoryManagement.addCompressedTextureObject(pixels.length);
                }
                break;

            case EDataTypeType.etc1ImageTexture:
                for (let level = 0; level < numMipmaps; level++) {
                    let width = Math.max(1, aTextureProperties.width / Math.pow(2, level));
                    let height = Math.max(1, aTextureProperties.height / Math.pow(2, level));
                    let etc1 = WebGLContext.getFeatureAsExtension("COMPRESSED_TEXTURE_ETC1");

                    let pixels = new Uint8Array(ETC1ImageDataLoader.getSizeFromResolution(width, height));
                    gl.compressedTexImage2D(gl.TEXTURE_2D,
                        level,
                        etc1.COMPRESSED_RGB_ETC1_WEBGL,
                        width,
                        height,
                        0,
                        pixels
                    );

                    this._gpuSize += MemoryManagement.addCompressedTextureObject(pixels.length);
                }
                break;
        }

        gl.bindTexture(gl.TEXTURE_2D, null);

        this.cache.dimension = [aTextureProperties.width, aTextureProperties.height];

        let glErr = checkGLError(gl);
        if (glErr !== 0) {
            this.cache.dimension = [0, 0];
            window.alert("Application received an OpenGL error no. " + glErr + " expecting problems with the model.");
            //window.location = "http://www.lexoview.com/";
        }

        return true;
    }

    public isReady(): boolean {
        return this._glTextureID !== null;
    }

    public render(aRenderPass: Pass, aIsFiltered?: boolean) {
        let shader: any = aRenderPass.getActiveShaderRenderable();
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        gl.activeTexture(gl.TEXTURE0);

        if (this._glTextureID && !aIsFiltered && (!this._sceneAPI.renderSettingsModule.getTexturesRenderingDisabled() || this._renderNode.getIsFromInternalModel())) {
            gl.bindTexture(gl.TEXTURE_2D, this._glTextureID);

            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, this.cache.glRepeatS);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, this.cache.glRepeatT);

            gl.uniform1i(shader.uniformLocations.u_texture_model, this.cache.model);

            gl.uniform1i(shader.uniformLocations.u_texture, 0);

            gl.uniform3fv(shader.uniformLocations.u_texture_blend_color, this._props.blendColor);

            if (this._props.glodLevelSize !== undefined) {
                gl.uniform1f(shader.uniformLocations.u_glod_level_size, this._props.glodLevelSize);
                gl.uniform4fv(shader.uniformLocations.u_tex_trafo, this._props.trafo);
            }

            gl.enableVertexAttribArray(shader.attributeLocations.a_tex_coord);

            gl.uniform2fv(shader.uniformLocations.u_texture_dimensions, this.cache.dimension);
        } else {
            gl.bindTexture(gl.TEXTURE_2D, this._renderGraph.getTemporaryGLResourceData().tempTextureID);
            gl.uniform1i(shader.uniformLocations.u_texture_model, 0);
        }
    }

    public finishRender(aRenderPass: Pass) {
        let shader: any = aRenderPass.getActiveShaderRenderable();
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, null);

        gl.disableVertexAttribArray(shader.attributeLocations.a_tex_coord);
    }
}

Factory.register(Texture);
